import { useEffect } from 'react';
import { CheckCircle2, XCircle, X } from 'lucide-react';
import clsx from 'clsx';
import { useAutoswitch } from '@/store/autoswitch';
import { useDeviceDisplayName } from '@/lib/displayName';

/**
 * Bottom-right floating region that surfaces switch-success / switch-failed
 * frames from the hub-wide `/api/wireless/autoswitch/events` socket.
 *
 * Auto-dismisses each toast after 6s (success) / 10s (failed). Users
 * can click the close button to dismiss early. Stack capped server-side
 * by the store (`RECENT_TOAST_CAP=6`); the older toasts shift off the
 * head if more arrive.
 */
const DISMISS_MS = { success: 6_000, failed: 10_000 } as const;

export function AutoswitchToasts(): JSX.Element | null {
  const toasts = useAutoswitch((s) => s.recentToasts);
  const dismiss = useAutoswitch((s) => s.dismissToast);

  useEffect(() => {
    // One timer per toast — straightforward and the cap (6) means
    // worst case is 6 simultaneous timers, which is negligible.
    const timers: number[] = [];
    for (const t of toasts) {
      const ttl = DISMISS_MS[t.kind];
      const elapsed = Date.now() - t.pushedAt;
      const remain = Math.max(0, ttl - elapsed);
      const id = window.setTimeout(() => dismiss(t.id), remain);
      timers.push(id);
    }
    return () => {
      for (const id of timers) window.clearTimeout(id);
    };
  }, [toasts, dismiss]);

  if (toasts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[340px] flex-col gap-2">
      {toasts.map((t) => (
        <AutoswitchToastRow key={t.id} toast={t} dismiss={dismiss} />
      ))}
    </div>
  );
}

interface AutoswitchToastRowProps {
  toast: ReturnType<typeof useAutoswitch.getState>['recentToasts'][number];
  dismiss: (id: number) => void;
}

function AutoswitchToastRow({ toast: t, dismiss }: AutoswitchToastRowProps): JSX.Element {
  const isOk = t.kind === 'success';
  const Icon = isOk ? CheckCircle2 : XCircle;
  const e = t.entry;
  // Same lookup the device list / sidebar use — alias → meta.model →
  // serial. Resolves the USB primary serial (e.g. FERSJ7EYMBMJLNRW)
  // to its human label (e.g. RMX3710) so the failure toast matches
  // the device card.
  const label = useDeviceDisplayName(e.usbSerial) || e.usbSerial;
  // Tiered-transport (May 2026): split the success copy by `path` so
  // the user knows whether the backup is a full-ADB wireless-debug
  // line ("everything works wirelessly") or a tcpip mirror-only
  // fallback ("scrcpy works, but other adb commands need USB").
  const successKindLabel =
    e.phase === 'success' && e.path === 'wireless'
      ? '无线调试'
      : e.phase === 'success' && e.path === 'tcpip'
        ? 'TCP-IP 镜像备线（仅镜像）'
        : 'adb 备线';
  const title = isOk
    ? `${label} · ${successKindLabel}已就绪`
    : `${label} · adb 备线建立失败`;
  const detail = (() => {
    if (e.phase === 'success') {
      return `${e.usbSerial} → ${e.wirelessSerial} · ${e.path} · ${e.durationMs} ms`;
    }
    if (e.phase === 'failed') {
      const paths = e.attemptedPaths.length > 0 ? e.attemptedPaths.join('+') : '(pre-flight)';
      return `${e.usbSerial} · 路径 ${paths} · ${e.message}`;
    }
    return e.usbSerial;
  })();
  return (
    <div
      className={clsx(
        'pointer-events-auto rounded-md border px-3 py-2 text-xs shadow-lg backdrop-blur',
        isOk
          ? 'border-emerald-500/30 bg-emerald-950/85 text-emerald-100'
          : 'border-rose-500/30 bg-rose-950/85 text-rose-100'
      )}
    >
      <div className="flex items-start gap-2">
        <Icon className={clsx('mt-0.5 h-3.5 w-3.5 shrink-0', isOk ? 'text-emerald-300' : 'text-rose-300')} />
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold">{title}</div>
          <div className="mt-0.5 break-words font-mono text-[11px] opacity-90">{detail}</div>
        </div>
        <button
          type="button"
          onClick={() => dismiss(t.id)}
          className="rounded p-0.5 text-current opacity-70 hover:opacity-100"
          aria-label="dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
